import { __ } from '../../utils';
// `Chip` z knižnice je chip na viacnásobný výber (`selected`, `onRemove`),
// určený do filtrov. Tu ide o značku s krížikom v poli, čo je MUI `Chip`
// s `onDelete` - rovnaká úvaha ako vo `FormAutocompleteField`.
import MuiChip from '@mui/material/Chip';
import {
  Box,
  ErrorMark,
  Field as FieldShell,
  TextField,
} from '@presov.sk/presov-ui-react';
import type { FieldValidator } from 'final-form';
import React from 'react';
import { useField } from 'react-final-form';

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  info?: React.ReactNode;
  disabled?: boolean;
  validate?: FieldValidator<string[]>;
};

/** Hrubá kontrola tvaru, tá istá ako na serveri (`normaliseReportSettings`). */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isEmailShape = (value: string): boolean =>
  EMAIL_SHAPE.test(value.trim());

/**
 * Zoznam e-mailových adries ako značky s krížikom.
 *
 * **`FormAutocompleteField` sa sem nehodí**, hoci `multiple` aj `freeSolo` má:
 * hodnotu si skladá z `options` (`options.filter((o) => value.includes(o.value))`),
 * takže voľne napísaná adresa, ktorá v ponuke nie je, by z poľa zmizla hneď po
 * potvrdení. Ponuka tu navyše nie je odkiaľ vziať - adresáti reportu nie sú
 * účty aplikácie ([[api-a-konfiguracia]]: zoznam účtov sa z prehliadača
 * nečíta).
 *
 * Rozpísaný text sa potvrdzuje `Enter`, čiarkou, bodkočiarkou aj medzerou
 * a **aj opustením poľa** - nedopísaná adresa, ktorá by sa pri uložení ticho
 * zahodila, je to najhoršie, čo tu môže nastať.
 *
 * Chybu nesie rám a koliesko „!" ako pri `FormTextField`; k tomu je ale
 * potrebný vlastný text pod poľom pre chybu **rozpísanej** adresy - tá ešte
 * nie je hodnotou poľa, takže by ju validácia `final-form` nevidela.
 */
const FormEmailListField: React.FC<Props> = ({
  name,
  label,
  placeholder,
  required,
  info,
  disabled = false,
  validate,
}) => {
  const {
    input: { value, onChange, onBlur },
    meta: { touched, error, submitError },
  } = useField<string[]>(name, { validate });

  const emails = React.useMemo<string[]>(
    () => (Array.isArray(value) ? value : []),
    [value],
  );

  const [draft, setDraft] = React.useState('');
  const [draftError, setDraftError] = React.useState<string | null>(null);

  const commit = React.useCallback(
    (raw: string): boolean => {
      const candidate = raw.trim().replace(/[,;]+$/, '');
      if (!candidate) {
        setDraft('');
        setDraftError(null);
        return true;
      }

      if (!isEmailShape(candidate)) {
        setDraftError(__('Toto nie je e-mailová adresa.'));
        return false;
      }

      // Duplicita nie je chyba, len sa nepridá druhýkrát - inak by tá istá
      // adresa dostala dva rovnaké maily.
      if (!emails.some((email) => email.toLowerCase() === candidate.toLowerCase())) {
        onChange([...emails, candidate]);
      }

      setDraft('');
      setDraftError(null);
      return true;
    },
    [emails, onChange],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ',', ';', ' '].includes(event.key)) {
      // `Enter` v poli formulára inak odošle celý formulár.
      event.preventDefault();
      commit(draft);
      return;
    }

    // Backspace na prázdnom vstupe zoberie poslednú značku - zaužívané
    // správanie polí so značkami a rýchlejšie než trafiť krížik.
    if (event.key === 'Backspace' && draft === '' && emails.length > 0) {
      onChange(emails.slice(0, -1));
    }
  };

  const errorMessage = touched ? error || submitError : submitError;
  const isError = !!errorMessage;
  const id = `id-${name}`;

  return (
    <FieldShell
      sx={{ mb: 0 }}
      label={label}
      htmlFor={id}
      required={required}
      info={info}
      error={errorMessage || null}
    >
      <Box
        sx={({ palette, shape }) => ({
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75,
          p: emails.length ? 1 : 0,
          pb: emails.length ? 0.25 : 0,
          borderRadius: `${shape.borderRadius}px`,
          ...(emails.length && {
            border: `1px solid ${isError ? palette.error.main : palette.divider}`,
            mb: 1,
          }),
        })}
      >
        {emails.map((email) => (
          <MuiChip
            key={email}
            label={email}
            size='small'
            onDelete={
              disabled
                ? undefined
                : () => onChange(emails.filter((item) => item !== email))
            }
            sx={{ mb: 0.75 }}
          />
        ))}
      </Box>

      <TextField
        id={id}
        type='email'
        fullWidth
        variant='outlined'
        value={draft}
        placeholder={placeholder ?? __('meno@presov.sk')}
        disabled={disabled}
        error={isError || !!draftError}
        onChange={(event) => {
          setDraft(event.target.value);
          if (draftError) setDraftError(null);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Rozpísaná adresa sa potvrdí aj odchodom z poľa. `onBlur` poľa sa
          // volá až potom, aby `touched` neprišlo skôr, než sa hodnota doplní.
          commit(draft);
          onBlur();
        }}
        slotProps={{
          input: {
            endAdornment: isError ? <ErrorMark message={errorMessage} /> : null,
          },
          htmlInput: {
            'aria-describedby': isError ? `${id}-err` : undefined,
          },
        }}
      />

      {draftError && (
        <Box
          component='span'
          sx={{ display: 'block', mt: 0.5, fontSize: 12, color: 'error.main' }}
        >
          {draftError}
        </Box>
      )}
    </FieldShell>
  );
};

export default FormEmailListField;
